import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Network,
  Save,
  Info,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  RotateCw,
  Lock,
  KeyRound,
} from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Tooltip } from "@/components/atoms/Tooltip";
import {
  profileService,
  type ProfileResponse,
  type LegalEntityHierarchy,
  type BranchHierarchy,
} from "@/services/profileService";
import { communicationChannelsService } from "@/services/communicationChannelsService";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import { useChatGatewaySyncStore } from "@/store/chatGatewaySyncStore";
import { useSettingsStore } from "@/store/settingsStore";
import { useProfileStore } from "@/store/profileStore";
import { useDisplayLegalEntities } from "@/hooks/useDisplayLegalEntities";
import { WhatsappOnboardingModal } from "./WhatsappOnboardingModal";
import { WhatsappNumberReplacementSection } from "../whatsappReplacement/WhatsappNumberReplacementSection";
import { ChannelEntityCardHeader } from "./communicationChannels/ChannelEntityCardHeader";
import { crmGatewaySyncService } from "@/services/crmGatewaySyncService";
import { chatGatewaySyncService } from "@/services/chatGatewaySyncService";
import { branchesService } from "@/services/branchesService";
import { toast } from "react-toastify";
import { showActionError } from "@/lib/feedback";

const CHANNEL_LOCKED_MESSAGE =
  "These WhatsApp credentials are locked. Please contact the support team to change them.";

const LEADS_PROCESSING_KEYS = [
  "LETTINGS",
  "SALES",
  "LETTINGS_VALUATION",
  "SALES_VALUATION",
  "GENERAL",
] as const;

function allLeadsProcessingDisabled(): Record<string, boolean> {
  return Object.fromEntries(LEADS_PROCESSING_KEYS.map((key) => [key, false]));
}

const notifyChannelLocked = () => toast.info(CHANNEL_LOCKED_MESSAGE, { autoClose: 4000 });

/**
 * True once every WhatsApp credential is persisted server-side. Mirrors Alto's
 * rule for CRM refs: a fully configured channel is immutable from the UI, since
 * changing a credential out from under an active channel breaks message
 * delivery and the chat-gateway registration that was built from it.
 * Partially filled rows stay editable so operators can finish them.
 */
const isChannelConfigured = (src: {
  whatsappPhone?: string | null;
  whatsappPhoneNumberId?: string | null;
  whatsappAccessToken?: string | null;
  whatsappBusinessAccountId?: string | null;
}) =>
  !!(
    src.whatsappPhone?.trim() &&
    src.whatsappPhoneNumberId?.trim() &&
    src.whatsappAccessToken?.trim() &&
    src.whatsappBusinessAccountId?.trim()
  );

/**
 * Read-only stand-in for a locked credential input. Clicking surfaces the same
 * "contact support" toast the CRM ref fields use. Secrets render masked — the
 * access token is never echoed back into the DOM once locked.
 */
const LockedFieldValue: React.FC<{
  value: string;
  mono?: boolean;
  masked?: boolean;
}> = ({ value, mono, masked }) => (
  <Tooltip content={CHANNEL_LOCKED_MESSAGE} side="top">
    <button
      type="button"
      onClick={notifyChannelLocked}
      className="h-10 w-full inline-flex items-center justify-between gap-2 px-3 text-left bg-slate-100 border border-bridge-border rounded-lg text-xs text-bridge-fg cursor-not-allowed hover:bg-slate-200 transition-colors"
    >
      <span className={`truncate ${mono ? "font-mono text-xs" : ""}`}>
        {masked ? "••••••••" : value || "—"}
      </span>
      <Lock className="w-4 h-4 text-bridge-muted shrink-0" />
    </button>
  </Tooltip>
);

/**
 * Field with a tiny uppercase label above the input — gives every WhatsApp
 * credential a labelled context instead of relying on placeholder-only hints.
 */
const Field: React.FC<{
  label: string;
  required?: boolean;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-1.5 min-w-0">
    <label className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider flex items-center gap-1">
      {label}
      {required && <span className="text-red-500 normal-case">*</span>}
    </label>
    {children}
    {hint && <div className="text-[11px] text-bridge-muted">{hint}</div>}
  </div>
);

export const CommunicationChannelList: React.FC = () => {
  const { keycloak } = useKeycloak();
  const email = keycloak?.tokenParsed?.email as string | undefined;
  // One email can belong to several organisations, and `/profile` returns a root for
  // each. Without the organisation the list showed every account's channels at once,
  // so the selected one is passed on the fetch and again on the render.
  const { legalEntityId } = useSettingsStore();
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  // Second guard on top of the scoped fetch: a cached tree from before an organisation
  // switch would otherwise render every account until the request came back.
  const displayEntities = useDisplayLegalEntities(profile?.legalEntities, legalEntityId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resuming, setResuming] = useState<string | null>(null);

  const sanitizePhone = (v: string) => v.replace(/[^\d+]/g, "");

  // Per-row input state, keyed by entity id (LE or branch).
  const [phones, setPhones] = useState<Record<string, string>>({});
  const [phoneNumberIds, setPhoneNumberIds] = useState<Record<string, string>>({});
  const [accessTokens, setAccessTokens] = useState<Record<string, string>>({});
  const [businessAccountIds, setBusinessAccountIds] = useState<Record<string, string>>({});

  const [enabledStates, setEnabledStates] = useState<Record<string, boolean>>({});

  // Ids (LE or branch) whose credentials are fully persisted, and therefore
  // locked. Derived from the server profile rather than held as its own state,
  // so every refresh path — mount, post-save, post-sync — agrees on what's
  // locked without extra bookkeeping. A save that completes the last missing
  // credential locks the block as soon as the refreshed profile lands.
  const lockedChannelIds = useMemo(() => {
    const locked = new Set<string>();
    const walk = (le: LegalEntityHierarchy) => {
      if (isChannelConfigured(le)) locked.add(le.id);
      le.branches.forEach((b) => {
        if (isChannelConfigured(b)) locked.add(b.id);
      });
      le.subEntities?.forEach(walk);
    };
    profile?.legalEntities.forEach(walk);
    return locked;
  }, [profile]);

  const localPhoneDuplicateErrors = useMemo(() => {
    const counts = new Map<string, number>();
    Object.values(phones).forEach(phone => {
      const p = phone?.trim();
      if (p) {
        counts.set(p, (counts.get(p) || 0) + 1);
      }
    });
    return counts;
  }, [phones]);

  const getLocalPhoneError = (phone?: string) => {
    const p = phone?.trim();
    if (!p) return null;
    
    // E.164 validation (e.g. +441342453254)
    if (!/^\+[1-9]\d{1,14}$/.test(p)) {
      return "Phone number must be in E.164 format (e.g. +441342453254)";
    }

    if ((localPhoneDuplicateErrors.get(p) || 0) > 1) {
      return "This phone number is duplicated in the current form.";
    }
    return null;
  };
  const hasLocalPhoneDuplicates = Array.from(localPhoneDuplicateErrors.values()).some(c => c > 1);

  const [onboardingTarget, setOnboardingTarget] = useState<{ id: string; name: string; type: "LE" | "BR"; legalEntityId: string } | null>(null);

  /** Persists after the floating sync panel dismisses — mirrors server {@code channelActive}. */
  const [syncActivated, setSyncActivated] = useState<Record<string, boolean>>({});
  const processedSyncJobIds = useRef(new Set<string>());

  const startSync = useChatGatewaySyncStore((s) => s.startSync);
  const syncJobs = useChatGatewaySyncStore((s) => s.jobs);
  const isSyncing = (id: string) => syncJobs.some((j) => j.targetId === id && !j.finishedAt);
  const isSyncCompleted = (id: string) =>
    syncJobs.some((j) => j.targetId === id && j.finishedAt && j.steps.every((s) => s.status === "done"));
  const isConversationEnabled = (id: string, channelActive?: boolean | null) =>
    !!(channelActive || syncActivated[id] || isSyncCompleted(id));

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const isExpanded = (id: string) => expandedIds.has(id);
  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const showError = (msg: string) => {
    showActionError(msg, { title: "Something went wrong" });
  };

  const triggerSync = (
    le: LegalEntityHierarchy,
    branch: BranchHierarchy | null,
    parentLeId: string | null,
  ) => {
    const phone = branch ? branch.whatsappPhone : le.whatsappPhone;
    if (!phone) return;

    const getAllDescendantBranches = (entity: LegalEntityHierarchy): BranchHierarchy[] => {
      return [
        ...entity.branches,
        ...(entity.subEntities || []).flatMap(getAllDescendantBranches),
      ];
    };

    const branches: BranchHierarchy[] = branch ? [branch] : getAllDescendantBranches(le);

    // whatsappAdapter.preSync fires the (best-effort) template sync — the store
    // owns that now so both channels route through one place.
    const channelId = branch ? branch.channelIds?.WHATSAPP : le.channelIds?.WHATSAPP;
    const credKey = branch?.id ?? le.id;

    startSync({
      channelType: "WHATSAPP",
      legalEntity: {
        id: le.id,
        name: le.name,
        primaryEmail: le.primaryEmail ?? null,
        parentId: parentLeId,
        sourceCrm: le.sourceCrm ?? "atlas",
      },
      branches: branches.map((b) => ({
        id: b.id,
        name: b.name,
        email: b.email?.trim() || null,
      })),
      channel: {
        branchId: branch?.id ?? null,
        whatsappPhone: phone,
        channelId: channelId ?? "",
        whatsappPhoneNumberId: phoneNumberIds[credKey] ?? null,
        whatsappAccessToken: accessTokens[credKey] ?? null,
        whatsappBusinessAccountId: businessAccountIds[credKey] ?? null,
      },
    });
  };

  const handleConnectClick = (id: string, name: string, type: "LE" | "BR", legalEntityId: string) => {
    setOnboardingTarget({ id, name, type, legalEntityId });
  };

  const getBranchIdsForEntity = (id: string, type: "LE" | "BR"): string[] => {
    if (type === "BR") return [id];
    const le =
      profile?.legalEntities.find((e) => e.id === id) ||
      profile?.legalEntities.flatMap((e) => e.subEntities || []).find((e) => e.id === id);
    if (!le) return [];
    return [
      ...le.branches.map((b) => b.id),
      ...(le.subEntities || []).flatMap((sub) => sub.branches.map((b) => b.id)),
    ];
  };

  const handleResume = async (id: string, type: "LE" | "BR", legalEntityId: string) => {
    setResuming(id);
    try {
      const branchId = type === "BR" ? id : null;
      await communicationChannelsService.onboardWhatsapp(null, legalEntityId, branchId, "CHATWOOT");
      toast.success("Successfully resumed WhatsApp onboarding");
      loadProfile();
    } catch (err: any) {
      showError(err?.response?.data?.message || err?.message || "Failed to resume WhatsApp onboarding");
    } finally {
      setResuming(null);
    }
  };

  useEffect(() => {
    if (!email) return;
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, legalEntityId]);

  useEffect(() => {
    if (!email) return;

    const newlyFinished = syncJobs.filter(
      (j) =>
        j.finishedAt &&
        j.steps.every((s) => s.status === "done") &&
        !processedSyncJobIds.current.has(j.id),
    );
    if (newlyFinished.length === 0) return;
    if (!email) return;

    for (const job of newlyFinished) {
      processedSyncJobIds.current.add(job.id);
    }

    setSyncActivated((prev) => {
      const next = { ...prev };
      for (const job of newlyFinished) {
        next[job.targetId] = true;
      }
      return next;
    });

    profileService
      .refresh(email, legalEntityId)
      .then((p) => {
        setProfile(p);
        useProfileStore.getState().applyProfileData(p);
      })
      .catch(() => {});
  }, [syncJobs, email, legalEntityId]);

  /**
   * @param force bypass the 5-minute profile cache. Required after anything
   *   that changes a channel server-side behind the UI's back — notably a
   *   completed or rolled-back WhatsApp number replacement, where a cached
   *   tree would keep rendering the previous number.
   */
  const loadProfile = async (force = false) => {
    try {
      const p = force
        ? await profileService.refresh(email!, legalEntityId)
        : await profileService.get(email!, legalEntityId);
      setProfile(p);
      useProfileStore.getState().applyProfileData(p);

      // Pre-fill every form field from the server's view of the channel rows.
      // Walking once and writing all four maps avoids four separate traversals.
      const ph: Record<string, string> = {};
      const pnId: Record<string, string> = {};
      const tok: Record<string, string> = {};
      const waba: Record<string, string> = {};
      const en: Record<string, boolean> = {};
      const activated: Record<string, boolean> = {};
      const markActivated = (id: string, src: { channelActive?: boolean | null }) => {
        if (src.channelActive) activated[id] = true;
      };
      const seed = (id: string, src: {
        whatsappPhone?: string | null;
        whatsappPhoneNumberId?: string | null;
        whatsappAccessToken?: string | null;
        whatsappBusinessAccountId?: string | null;
        whatsappEnabled?: boolean | null;
        channelActive?: boolean | null;
      }) => {
        if (src.whatsappPhone) ph[id] = sanitizePhone(src.whatsappPhone);
        if (src.whatsappPhoneNumberId) pnId[id] = src.whatsappPhoneNumberId;
        if (src.whatsappAccessToken) tok[id] = src.whatsappAccessToken;
        if (src.whatsappBusinessAccountId) waba[id] = src.whatsappBusinessAccountId;
        en[id] = !!src.whatsappEnabled;
        markActivated(id, src);
      };
      const walkLe = (le: LegalEntityHierarchy) => {
        seed(le.id, le);
        le.branches.forEach((b) => seed(b.id, b));
        le.subEntities?.forEach(walkLe);
      };
      p.legalEntities.forEach(walkLe);
      setPhones(ph);
      setPhoneNumberIds(pnId);
      setAccessTokens(tok);
      setBusinessAccountIds(waba);
      setEnabledStates(en);
      setSyncActivated(activated);
    } catch (err) {
      showError("Failed to load hierarchy");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (
    id: string,
    type: "LE" | "BR",
    parentLeId?: string,
    sourceCrm?: string,
    explicitEnabledState?: boolean,
    extraSyncData?: { name: string; channelId: string | null; isSyncDone: boolean }
  ) => {
    setSaving(id);
    setSuccess(null);

    if (hasLocalPhoneDuplicates) {
      toast.error("Please fix duplicate phone numbers before saving.");
      setSaving(null);
      return;
    }

    try {
      const branchIdsToCheck = getBranchIdsForEntity(id, type);
      const phone = phones[id] || "";
      const leId = type === "LE" ? id : parentLeId!;
      const brId = type === "LE" ? null : id;
      const enabled = explicitEnabledState !== undefined ? explicitEnabledState : (enabledStates[id] || false);

      await communicationChannelsService.upsert({
        channelType: "WHATSAPP",
        legalEntityId: leId,
        branchId: brId,
        whatsappPhone: phone,
        sourceCrm: sourceCrm,
        whatsappPhoneNumberId: phoneNumberIds[id] ?? null,
        whatsappAccessToken: accessTokens[id] ?? null,
        whatsappBusinessAccountId: businessAccountIds[id] ?? null,
        whatsappEnabled: enabled,
      });

      if (branchIdsToCheck.length > 0) {
        let syncReady = false;
        try {
          const readiness = await crmGatewaySyncService.checkSyncReadiness({
            branchIds: branchIdsToCheck,
          });
          syncReady = !!readiness.ready;
        } catch {
          syncReady = false;
        }

        if (!syncReady) {
          const disabledConfig = allLeadsProcessingDisabled();
          const results = await Promise.allSettled(
            branchIdsToCheck.map((branchId) =>
              branchesService.update(branchId, { leadsProcessingConfig: disabledConfig }),
            ),
          );
          if (results.some((result) => result.status === "rejected")) {
            toast.error("Failed to disable lead processing for some branches.");
          }
        }
      }

      if (extraSyncData?.isSyncDone && explicitEnabledState !== undefined && extraSyncData.channelId) {
        if (phone && phoneNumberIds[id] && accessTokens[id] && businessAccountIds[id]) {
          await chatGatewaySyncService.syncChannel({
            channelType: "WHATSAPP",
            legalEntityId: leId,
            branchId: brId,
            whatsappPhone: phone,
            whatsappEnabled: explicitEnabledState,
            sourceCrm: sourceCrm ?? "atlas",
            channelId: extraSyncData.channelId,
            whatsappPhoneNumberId: phoneNumberIds[id],
            whatsappAccessToken: accessTokens[id],
            whatsappBusinessAccountId: businessAccountIds[id],
          });
          await chatGatewaySyncService.whatsappBranch({
            name: extraSyncData.name,
            legalEntityId: leId,
            branchId: brId || "",
            channelId: extraSyncData.channelId,
            whatsappPhoneNumber: phone,
            whatsappPhoneNumberId: phoneNumberIds[id],
            whatsappBusinessAccountId: businessAccountIds[id],
            whatsappAccessToken: accessTokens[id],
            active: explicitEnabledState,
          });
        }
      }

      setSuccess(id);
      setTimeout(() => setSuccess(null), 3000);
      const p = await profileService.refresh(email!, legalEntityId);
      setProfile(p);
      useProfileStore.getState().applyProfileData(p);
    } catch (err: any) {
      // API typically responds with a message when 409 Conflict is thrown
      const backendError = err?.response?.data?.message || err?.message;
      showError(backendError ?? "Failed to save phone number");
      
      // Revert optimistic toggle state if it was an explicit toggle
      if (explicitEnabledState !== undefined) {
        setEnabledStates((prev) => ({ ...prev, [id]: !explicitEnabledState }));
      }
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bridge-accent"></div>
      </div>
    );
  }

  /**
   * Renders the WhatsApp Business form + action footer for a single entity
   * (LE or branch). Layout is the same for both — only the header chrome
   * and the action handler bindings differ.
   *
   * Co-locating the credentials with their owning entity avoids the previous
   * "credentials strip parked under all branches" pattern, which made it
   * unclear which row a strip referred to.
   */
  const renderConfigPanel = (opts: {
    id: string;
    name: string;
    kind: "LE" | "BR";
    agencyRef?: string | null;
    channelActive?: boolean | null;
    sourceCrm?: string | null;
    legalEntityId: string;
    channelId?: string | null;
    whatsappTemplateSetupError?: string | null;
    onSyncForConversation: () => void;
  }) => {
    const {
      id,
      name,
      kind,
      agencyRef,
      channelActive,
      sourceCrm,
      legalEntityId,
      channelId,
      whatsappTemplateSetupError,
      onSyncForConversation,
    } = opts;
    const phoneValue = phones[id] || "";
    const isSyncDone = isConversationEnabled(id, channelActive);
    const hasToken = !!accessTokens[id]?.trim();
    const hasAllCreds = !!(phones[id]?.trim() && phoneNumberIds[id]?.trim() && accessTokens[id]?.trim() && businessAccountIds[id]?.trim());
    const isFullyOnboarded = hasAllCreds && enabledStates[id] === true;
    const locked = lockedChannelIds.has(id);

    return (
      <div className="px-5 py-4 space-y-5">
        {!agencyRef && (
          <Link
            to="/integrations"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-700 hover:text-amber-900 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-md mb-2"
          >
            <Lock className="w-3 h-3" />
            Integrate CRM first to enable this section
          </Link>
        )}

        <div className="bg-slate-50/50 rounded-2xl border border-bridge-border p-4 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-bridge-border/40">
            <h4 className="text-xs font-semibold text-bridge-fg flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              WhatsApp Configuration
              {locked && (
                <Tooltip content={CHANNEL_LOCKED_MESSAGE} side="top">
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-bridge-border px-2 py-0.5 rounded-md">
                    <Lock className="w-3 h-3" />
                    Locked
                  </span>
                </Tooltip>
              )}
            </h4>
          </div>

          {whatsappTemplateSetupError && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 flex gap-2.5 text-amber-800 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
              <div>
                <p className="font-bold text-xs uppercase tracking-wider mb-0.5">Template Setup Notice</p>
                <p className="text-xs">{whatsappTemplateSetupError}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            <Field label="WhatsApp Phone" required>
              {locked ? (
                <LockedFieldValue value={phoneValue} />
              ) : (
                <div className="flex flex-col gap-1 w-full">
                  <Input
                    placeholder="e.g. +441342453254"
                    className={`h-10 transition-colors ${getLocalPhoneError(phoneValue) ? "border-red-400 focus:border-red-500 ring-1 ring-red-200" : ""}`}
                    value={phoneValue}
                    onChange={(e) => setPhones({ ...phones, [id]: sanitizePhone(e.target.value) })}
                  />
                  {getLocalPhoneError(phoneValue) && <div className="text-[11px] text-red-600 leading-tight">{getLocalPhoneError(phoneValue)}</div>}
                </div>
              )}
            </Field>

            <Field
              label="Phone Number ID"
              hint="Meta phone number ID (15–20 digit numeric)"
            >
              {locked ? (
                <LockedFieldValue value={phoneNumberIds[id] || ""} mono />
              ) : (
                <Input
                  placeholder="123456789012345"
                  className={`h-10 transition-colors`}
                  value={phoneNumberIds[id] || ""}
                  autoComplete="off"
                  onChange={(e) => setPhoneNumberIds({ ...phoneNumberIds, [id]: e.target.value })}
                />
              )}
            </Field>

            <Field label="Access Token" hint="Long-lived WhatsApp Business token">
              {locked ? (
                <LockedFieldValue value={accessTokens[id] || ""} mono masked />
              ) : (
                <Input
                  leading={<KeyRound className="h-4 w-4 text-bridge-muted/60" />}
                  placeholder="EAAxxxxxxxxxxxx…"
                  type="password"
                  className="font-mono text-xs"
                  value={accessTokens[id] || ""}
                  autoComplete="new-password"
                  onChange={(e) => setAccessTokens({ ...accessTokens, [id]: e.target.value })}
                />
              )}
            </Field>

            <Field label="WhatsApp Business Account ID" hint="WABA UUID">
              {locked ? (
                <LockedFieldValue value={businessAccountIds[id] || ""} mono />
              ) : (
                <Input
                  placeholder="abc12345-…"
                  className={`h-10 transition-colors`}
                  value={businessAccountIds[id] || ""}
                  autoComplete="off"
                  onChange={(e) => setBusinessAccountIds({ ...businessAccountIds, [id]: e.target.value })}
                />
              )}
            </Field>
          </div>

          {/*
            The credentials above stay locked and read-only once configured.
            Replacing the number is not an edit of that form — it is a durable
            Atlas-side cutover, so it gets its own deliberate action and its
            own state, both driven entirely by Atlas.
          */}
          {locked && channelId && (
            <div className="pt-4 border-t border-bridge-border/40">
              <WhatsappNumberReplacementSection
                channelId={channelId}
                entityName={name}
                currentPhone={phoneValue}
                currentPhoneNumberId={phoneNumberIds[id]}
                currentWabaId={businessAccountIds[id]}
                onChannelDataStale={() => {
                  void loadProfile(true);
                }}
              />
            </div>
          )}
        </div>

        {/* ── Footer: actions ────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2.5 pt-1">
          {/* Locked rows have nothing editable left to submit — the Active
              toggle saves itself, so Save would be a no-op. */}
          {!locked && (
            <Button
              size="sm"
              variant="secondary"
              loading={saving === id}
              disabled={!phones[id]?.trim()}
              onClick={() => handleSave(id, kind, kind === "BR" ? legalEntityId : id, sourceCrm || undefined)}
              className="rounded-xl h-9 px-4"
            >
              <Save className="h-4 w-4 shrink-0" />
              Save
            </Button>
          )}

          {!hasToken ? (
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white rounded-xl h-9 px-4 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-green-600/50"
              onClick={() => handleConnectClick(id, name, kind, legalEntityId)}
            >
              <ExternalLink className="h-4 w-4 shrink-0" />
              Connect
            </Button>
          ) : isFullyOnboarded ? (
            <Button
              size="sm"
              className="bg-green-600/50 text-white rounded-xl h-9 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled
              title="WhatsApp Business is already connected."
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Connected
            </Button>
          ) : (
            <Button
              size="sm"
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl h-9 px-4"
              loading={resuming === id}
              onClick={() => handleResume(id, kind, legalEntityId)}
            >
              <RotateCw className="h-4 w-4 shrink-0" />
              Resume Onboarding
            </Button>
          )}

          <Button
            size="sm"
            variant="secondary"
            loading={isSyncing(id)}
            disabled={!(phones[id]?.trim() && phoneNumberIds[id]?.trim() && accessTokens[id]?.trim() && businessAccountIds[id]?.trim())}
            title={!(phones[id]?.trim() && phoneNumberIds[id]?.trim() && accessTokens[id]?.trim() && businessAccountIds[id]?.trim()) ? "Please fill in all 4 WhatsApp fields (Phone, Phone Number ID, Access Token, and WABA ID) above to enable this." : undefined}
            onClick={onSyncForConversation}
            className="rounded-xl h-9 px-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCw className="h-4 w-4 shrink-0" />
            {isSyncDone ? "Resync" : "Enable Conversations"}
          </Button>

          {success === id && (
            <span className={`inline-flex items-center gap-1 text-xs font-bold text-green-600 ${isSyncDone ? '' : 'ml-auto'}`}>
              <CheckCircle2 className="w-4 h-4" />
              Saved
            </span>
          )}

          {isSyncDone && (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-200 ml-auto">
              <CheckCircle2 className="w-4 h-4" />
              Historical Data Synced
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderWhatsAppEntityHeader = (opts: {
    id: string;
    name: string;
    typeLabel: string;
    variant: "le" | "branch";
    channelActive?: boolean | null;
    kind: "LE" | "BR";
    legalEntityId: string;
    sourceCrm?: string | null;
    channelId?: string | null;
  }) => {
    const { id, name, typeLabel, variant, channelActive, kind, legalEntityId, sourceCrm, channelId } = opts;
    const isSyncDone = isConversationEnabled(id, channelActive);
    const configured = lockedChannelIds.has(id);
    const connected = isSyncDone && !!enabledStates[id];

    return (
      <ChannelEntityCardHeader
        name={name}
        typeLabel={typeLabel}
        variant={variant}
        expanded={isExpanded(id)}
        onToggleExpand={() => toggleExpanded(id)}
        configured={configured}
        connected={connected}
        enabled={!!enabledStates[id]}
        toggleDisabled={!isSyncDone}
        toggleDisabledTitle="Enable conversations before activating this channel"
        onEnabledChange={(newVal) => {
          setEnabledStates({ ...enabledStates, [id]: newVal });
          handleSave(id, kind, kind === "BR" ? legalEntityId : id, sourceCrm || undefined, newVal, {
            name,
            channelId: channelId || null,
            isSyncDone,
          });
        }}
        icon={
          variant === "le" ? (
            <Building2 className="h-4 w-4 shrink-0 text-bridge-accent" />
          ) : (
            <Network className="h-3.5 w-3.5 shrink-0 text-bridge-muted" />
          )
        }
      />
    );
  };

  /** Recursive renderer. Depth drives indentation only; data flow unchanged. */
  const renderLegalEntityCard = (
    le: LegalEntityHierarchy,
    depth: number,
    parentLeId: string | null,
  ): React.ReactNode => (
    <React.Fragment key={le.id}>
      <div
        className="bg-white rounded-2xl border border-bridge-border overflow-hidden shadow-sm"
        style={{ marginLeft: depth * 24 }}
      >
        {/* ── LE header ────────────────────────────────────────────────── */}
        {renderWhatsAppEntityHeader({
          id: le.id,
          name: le.name,
          typeLabel: depth === 0 ? "Legal Entity" : "Sub Legal Entity",
          variant: "le",
          channelActive: le.channelActives?.WHATSAPP,
          kind: "LE",
          legalEntityId: le.id,
          sourceCrm: le.sourceCrm,
          channelId: le.channelIds?.WHATSAPP,
        })}

        {/* ── LE config (form + actions) ───────────────────────────────── */}
        {isExpanded(le.id) &&
          renderConfigPanel({
            id: le.id,
            name: le.name,
            kind: "LE",
            agencyRef: le.agencyRef,
            channelActive: le.channelActives?.WHATSAPP,
            sourceCrm: le.sourceCrm,
            legalEntityId: le.id,
            channelId: le.channelIds?.WHATSAPP,
            whatsappTemplateSetupError: le.whatsappTemplateSetupError,
            onSyncForConversation: () => triggerSync(le, null, parentLeId),
          })}

        {/* ── Branches section ─────────────────────────────────────────── */}
        {(le.branches.length > 0 || (le.subEntities?.length ?? 0) > 0) && (
          <div className="border-t border-bridge-border bg-slate-50/30 px-5 py-2 flex items-center gap-2">
            <Network className="w-3.5 h-3.5 text-bridge-muted" />
            <span className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider">
              Branches ({le.branches.length})
            </span>
          </div>
        )}

        <div className="divide-y divide-bridge-border/50">
          {le.branches.map((branch) => (
            <div key={branch.id}>
              {renderWhatsAppEntityHeader({
                id: branch.id,
                name: branch.name,
                typeLabel: "Branch",
                variant: "branch",
                channelActive: branch.channelActives?.WHATSAPP,
                kind: "BR",
                legalEntityId: le.id,
                sourceCrm: branch.sourceCrm || le.sourceCrm,
                channelId: branch.channelIds?.WHATSAPP,
              })}
              {isExpanded(branch.id) &&
                renderConfigPanel({
                  id: branch.id,
                  name: branch.name,
                  kind: "BR",
                  agencyRef: branch.agencyRef,
                  channelActive: branch.channelActives?.WHATSAPP,
                  sourceCrm: branch.sourceCrm || le.sourceCrm,
                  legalEntityId: le.id,
                  channelId: branch.channelIds?.WHATSAPP,
                  whatsappTemplateSetupError: branch.whatsappTemplateSetupError,
                  onSyncForConversation: () => triggerSync(le, branch, parentLeId),
                })}
            </div>
          ))}
          {le.branches.length === 0 && (le.subEntities?.length ?? 0) === 0 && (
            <div className="px-5 py-3 text-[11px] text-bridge-muted italic">No branches configured for this legal entity.</div>
          )}
        </div>
      </div>

      {/* Sub-LEs (recursive). */}
      {le.subEntities?.map((sub) => renderLegalEntityCard(sub, depth + 1, le.id))}
    </React.Fragment>
  );

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3 flex gap-2.5 text-blue-800 text-xs">
          <Info className="w-4 h-4 shrink-0 text-blue-400" />
          <p>
            Configure WhatsApp Business for your Legal Entities and Branches. Fill in the WhatsApp phone and Meta
            credentials, click <strong>Save</strong>, then click <strong>Connect</strong> to begin the Meta onboarding
            process. Use <strong>Enable Conversations</strong> once historical data has loaded.
          </p>
        </div>

      <div className="space-y-4">
        {displayEntities.map((le) => renderLegalEntityCard(le, 0, null))}
      </div>

      {onboardingTarget && (
        <WhatsappOnboardingModal
          isOpen={!!onboardingTarget}
          onClose={() => setOnboardingTarget(null)}
          entityName={onboardingTarget.name}
          entityId={onboardingTarget.id}
          entityType={onboardingTarget.type}
          legalEntityId={onboardingTarget.legalEntityId}
        />
      )}
      </div>
    </>
  );
};